// Public complimentary-state helpers.
//
// These helpers deliberately require the API's explicit commercial contract.
// A zero-looking amount, a missing Invoice, or a browser-calculated discount is
// never enough to classify an Order or Checkout Session as complimentary.

import type { CheckoutStatusResponse } from "./stage2Api";
import type {
  OrderMoneyBreakdown,
  OrderMoneyTriple,
  OrderSoldComponent,
  PublicOrder,
} from "./types";

export type ComplimentaryReleaseState = "released" | "pending" | "blocked";

function hasNotRequiredStatus(value: unknown): boolean {
  return value === "not_required";
}

/**
 * Public responses may carry a nested status and a legacy flat mirror. Accept
 * either representation during rollout, but fail closed if both explicitly
 * disagree; a conflicting financial graph is not proof of a complimentary
 * entitlement.
 */
function hasConsistentNotRequiredStatus(nested: unknown, flat: unknown): boolean {
  const nestedPresent = typeof nested === "string";
  const flatPresent = typeof flat === "string";
  if (nestedPresent && flatPresent && nested !== flat) return false;
  return hasNotRequiredStatus(nested) || hasNotRequiredStatus(flat);
}

function commercialContract(value: {
  commercialState?: unknown;
  paymentRequired?: unknown;
  invoiceRequired?: unknown;
}): boolean {
  return (
    value.commercialState === "complimentary" &&
    value.paymentRequired === false &&
    value.invoiceRequired === false
  );
}

/** True only for an explicitly server-authorised complimentary Checkout status. */
export function isComplimentaryCheckoutStatus(
  status: CheckoutStatusResponse | null | undefined,
): status is CheckoutStatusResponse & {
  commercialState: "complimentary";
  paymentRequired: false;
  invoiceRequired: false;
} {
  if (!status || !commercialContract(status)) return false;
  return (
    hasConsistentNotRequiredStatus(status.payment?.status, status.paymentStatus) &&
    hasConsistentNotRequiredStatus(status.invoice?.status, status.invoiceStatus)
  );
}

/** True only for the exact public Order contract; never infer from money. */
export function isComplimentaryOrder(order: PublicOrder | null | undefined): boolean {
  if (!order || !commercialContract(order)) return false;
  return (
    hasConsistentNotRequiredStatus(order.payment?.paymentStatus, order.paymentStatus) &&
    hasConsistentNotRequiredStatus(order.invoice?.invoiceStatus, order.invoiceStatus)
  );
}

/**
 * A distinct server-declared fail-closed Order Hub state. It is deliberately
 * not a complimentary entitlement, but it must suppress all financial UI.
 */
export function isCommerciallyIncoherentOrder(order: PublicOrder | null | undefined): boolean {
  return !!(
    order &&
    order.commercialState === "incoherent" &&
    order.paymentRequired === false &&
    order.invoiceRequired === false
  );
}

export function complimentaryReleaseStateOf(
  value:
    | {
        release?: { state?: unknown; status?: unknown } | null;
        complimentaryReleaseState?: unknown;
        state?: unknown;
        nextAction?: unknown;
      }
    | null
    | undefined,
): ComplimentaryReleaseState | null {
  // A blocked/support signal is a stronger customer-safety constraint than a
  // stale optimistic release mirror. Never advertise access when those fields
  // disagree.
  if (
    value?.state === "complimentary_blocked" ||
    value?.state === "complimentary_release_blocked" ||
    value?.nextAction === "contact_support"
  ) {
    return "blocked";
  }
  const releaseState = value?.release?.state;
  const releaseStatus = value?.release?.status;
  const directRelease = value?.complimentaryReleaseState;
  if (releaseState === "blocked" || releaseStatus === "blocked" || directRelease === "blocked") {
    return "blocked";
  }
  if (
    value?.state === "complimentary_pending_release" ||
    value?.state === "complimentary_release_pending"
  ) {
    return "pending";
  }
  if (
    releaseState === "pending" ||
    releaseStatus === "pending" ||
    releaseStatus === "release_pending" ||
    directRelease === "pending"
  ) {
    return "pending";
  }
  if (
    value?.state === "complimentary_complete" ||
    value?.state === "complimentary_access_released"
  ) {
    return "released";
  }
  if (
    releaseState === "released" ||
    releaseStatus === "released" ||
    releaseStatus === "access_released" ||
    directRelease === "released"
  ) {
    return "released";
  }
  return null;
}

function displayMoney(value: string | number | null | undefined): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,2})?$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function displayMoneyTriple(
  value: OrderMoneyTriple | null | undefined,
): { net: number; tax: number; gross: number } | null {
  const net = displayMoney(value?.net);
  const tax = displayMoney(value?.tax);
  const gross = displayMoney(value?.gross);
  if (net === null || tax === null || gross === null) return null;
  return { net, tax, gross };
}

export interface ComplimentarySoldComponentDisplay {
  name: string;
  componentType: string | null;
  quantity: number;
  currency: string;
  original: { net: number; tax: number; gross: number };
  discount: { net: number; tax: number; gross: number };
  final: { net: number; tax: number; gross: number };
}

/**
 * Format only complete, server-owned component snapshots. The client neither
 * creates a generic pass nor derives a discount: every displayed figure comes
 * directly from the supplied public sold-component projection.
 */
export function complimentarySoldComponentsForDisplay(
  components: OrderSoldComponent[] | null | undefined,
): ComplimentarySoldComponentDisplay[] | null {
  if (!Array.isArray(components) || components.length === 0) return null;
  const display: ComplimentarySoldComponentDisplay[] = [];
  for (const component of components) {
    const name = typeof component.name === "string" ? component.name.trim() : "";
    const currency =
      typeof component.currency === "string" ? component.currency.trim().toUpperCase() : "";
    const quantity = component.quantity;
    const original = displayMoneyTriple(component.original);
    const discount = displayMoneyTriple(component.discount);
    const final = displayMoneyTriple(component.final);
    if (
      !name ||
      !/^[A-Z]{3}$/.test(currency) ||
      typeof quantity !== "number" ||
      !Number.isSafeInteger(quantity) ||
      quantity < 1 ||
      !original ||
      !discount ||
      !final
    ) {
      return null;
    }
    display.push({
      name,
      componentType:
        typeof component.componentType === "string" && component.componentType.trim()
          ? component.componentType.trim()
          : null,
      quantity,
      currency,
      original,
      discount,
      final,
    });
  }
  return display;
}

/**
 * Return display-ready, server-owned sold evidence only when all three figures
 * and a currency are present. It intentionally performs no reconciliation or
 * discount calculation in the browser.
 */
export function complimentaryMoneyForDisplay(breakdown: OrderMoneyBreakdown | null | undefined): {
  currency: string;
  originalGross: number;
  discountTotal: number;
  finalGross: number;
  discountLabel: string | null;
} | null {
  const currency =
    typeof breakdown?.currency === "string" ? breakdown.currency.trim().toUpperCase() : "";
  const originalGross = displayMoney(breakdown?.originalGross);
  const discountTotal = displayMoney(breakdown?.discountTotal);
  const finalGross = displayMoney(breakdown?.finalGross);
  if (
    !/^[A-Z]{3}$/.test(currency) ||
    originalGross === null ||
    discountTotal === null ||
    finalGross === null
  ) {
    return null;
  }
  const label =
    typeof breakdown?.discountLabel === "string" && breakdown.discountLabel.trim()
      ? breakdown.discountLabel.trim()
      : null;
  return { currency, originalGross, discountTotal, finalGross, discountLabel: label };
}
